import { Badge, Card, CardContent, CardHeader, CardTitle } from "@/ui";
import { 
  Shield, 
  Lock, 
  Database, 
  Zap, 
  CheckCircle,
  Globe,
  FileText
} from "lucide-react";
const integrationImage = "/assets/website/images/Healthcare_technology_integration_partners_09acd517.png";

const integrationPartners = [
  {
    category: "EHR Systems",
    partners: ["Coming Soon", "Integration", "In Progress", "Development", "Phase"],
    description: "Seamless integration with major electronic health record systems"
  },
  {
    category: "Healthcare Standards",
    partners: ["HL7 FHIR", "C-CDA", "DICOM", "SNOMED CT", "ICD-10"],
    description: "Full compliance with healthcare interoperability standards"
  },
  {
    category: "Wellness Apps",
    partners: ["Coming Soon", "Integration", "In Progress", "Development", "Phase"],
    description: "Connect with popular health and wellness applications"
  }
];

const securityFeatures = [
  {
    icon: Shield,
    title: "HIPAA Compliance",
    description: "End-to-end encryption and privacy controls",
    details: ["256-bit encryption", "Access logging", "Audit trails", "BAA agreements"]
  },
  {
    icon: Lock,
    title: "Multi-Tenant Isolation",
    description: "Complete data separation between organizations",
    details: ["Tenant-scoped access", "Isolated storage", "Network segmentation", "Identity management"]
  },
  {
    icon: Database,
    title: "Privacy-First Storage",
    description: "Minimal data retention with automatic purging",
    details: ["Data minimization", "Automated purging", "Anonymization", "Consent management"]
  },
  {
    icon: Zap,
    title: "Real-Time Monitoring",
    description: "24/7 security monitoring and threat detection",
    details: ["Intrusion detection", "Anomaly monitoring", "Incident response", "SOC2 compliant"]
  }
];

const complianceStandards = [
  { name: "HIPAA", status: "", description: "Health Insurance Portability and Accountability Act" },
  { name: "SOC2 Type II", status: "", description: "Security, Availability, and Confidentiality" },
  { name: "ISO 27001", status: "", description: "Information Security Management" },
  { name: "GDPR", status: "", description: "General Data Protection Regulation" }
];

export default function IntegrationSecurity() {

  return (
    <section id="integration-security" className="py-20">
      {/* Hero Image Section */}
      <div className="relative h-80 mb-20 overflow-hidden">
        <img 
          src={integrationImage} 
          alt="Healthcare Technology Integration Partners"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Badge variant="secondary" className="mb-4" data-testid="badge-integration">
              Integration & Security
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4" data-testid="text-integration-title">
              Enterprise-Ready Healthcare Interoperability
            </h2>
            <p className="text-xl text-foreground max-w-3xl mx-auto" data-testid="text-integration-description">
              Seamlessly connect with existing healthcare infrastructure while maintaining the highest 
              standards of security and compliance.
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto max-w-7xl bg-muted/30 py-16 rounded-lg">

        {/* Integration Partners */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-center mb-8" data-testid="text-partners-title">
            Healthcare Integration Partners
          </h3>
          <div className="grid md:grid-cols-3 gap-8 place-items-center justify-items-center">
            {integrationPartners.map((category, index) => (
              <Card key={index} className="hover-elevate" data-testid={`card-integration-${index}`}>
                <CardHeader>
                  <CardTitle className="text-lg" data-testid={`text-category-title-${index}`}>
                    {category.category}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground" data-testid={`text-category-description-${index}`}>
                    {category.description}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {category.partners.map((partner, partnerIndex) => (
                      <Badge key={partnerIndex} variant="outline" className="text-xs" data-testid={`badge-partner-${index}-${partnerIndex}`}>
                        {partner}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Security Features */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-center mb-8" data-testid="text-security-title">
            Security & Privacy Features
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 place-items-center justify-items-center">
            {securityFeatures.map((feature, index) => (
              <Card key={index} className="text-center hover-elevate relative" data-testid={`card-security-${index}`}>
                {index === 0 && (
                  <div className="absolute top-2 right-2 text-red-600 font-bold text-lg" data-testid="text-hipaa-note">
                    *
                  </div>
                )}
                <CardHeader>
                  <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg mx-auto mb-4">
                    <feature.icon className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-lg" data-testid={`text-security-title-${index}`}>
                    {feature.title}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground" data-testid={`text-security-description-${index}`}>
                    {feature.description}
                  </p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {feature.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-center justify-center" data-testid={`text-security-detail-${index}-${detailIndex}`}>
                        <CheckCircle className="h-3 w-3 text-primary mr-2 flex-shrink-0" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Compliance Standards */}
        <div className="bg-background rounded-lg p-8 mb-12 max-w-6xl mx-auto relative">
          <div className="absolute top-4 right-4 text-red-600 font-bold text-lg" data-testid="text-compliance-note">
            *
          </div>
          <h3 className="text-2xl font-bold text-center mb-8" data-testid="text-compliance-title">
            Designing for Compliance & Certifications
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 place-items-center justify-items-center">
            {complianceStandards.map((standard, index) => (
              <div key={index} className="text-center" data-testid={`compliance-${index}`}>
                <div className="flex items-center justify-center w-16 h-16 bg-accent/20 rounded-lg mx-auto mb-4">
                  <FileText className="h-8 w-8 text-accent-foreground" />
                </div>
                <h4 className="font-semibold text-foreground mb-1" data-testid={`compliance-name-${index}`}>
                  {standard.name}
                </h4>
                <p className="text-xs text-muted-foreground" data-testid={`compliance-description-${index}`}>
                  {standard.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Deployment Options */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card className="hover-elevate" data-testid="card-deployment-cloud">
            <CardHeader>
              <div className="flex items-center gap-3 mb-3">
                <Globe className="h-8 w-8 text-primary" />
                <div>
                  <CardTitle>Cloud Deployment</CardTitle>
                  <p className="text-sm text-muted-foreground">Fully managed SaaS solution</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-primary mr-2" />
                  Rapid deployment and scaling
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-primary mr-2" />
                  Automatic updates and maintenance
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-primary mr-2" />
                  99.9% uptime SLA
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-primary mr-2" />
                  Multi-region availability
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="hover-elevate" data-testid="card-deployment-onprem">
            <CardHeader>
              <div className="flex items-center gap-3 mb-3">
                <Database className="h-8 w-8 text-primary" />
                <div>
                  <CardTitle>On-Premises Deployment</CardTitle>
                  <p className="text-sm text-muted-foreground">Complete control and customization</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-primary mr-2" />
                  Full data sovereignty
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-primary mr-2" />
                  Custom security configurations
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-primary mr-2" />
                  Air-gapped deployment options
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-primary mr-2" />
                  Dedicated support team
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

      </div>
    </section>
  );
}